import type { Area } from "@/ui-adapters/EasyCrop";

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.src = url;
  });

function getRadianAngle(degreeValue: number): number {
  return (degreeValue * Math.PI) / 180;
}

function rotateSize(width: number, height: number, rotation: number): { width: number; height: number } {
  const rotRad = getRadianAngle(rotation);

  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

export async function getCroppedImg(
  imgFile: File,
  url: string,
  pixelCrop: Area,
  rotation = 0,
  flip = { horizontal: false, vertical: false },
  { allowPadding = true }: { allowPadding?: boolean } = {},
): Promise<File | null> {
  const image = await createImage(url);
  const { width, height } = rotateSize(image.width, image.height, rotation);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width);
  canvas.height = Math.round(height);
  const { width: bBoxWidth, height: bBoxHeight } = canvas;

  // Never persist an oversized transparent canvas for a crop that must fill the image.
  // The cropper rounds its pixel bounds, so use the same rounding here.
  if (
    !allowPadding &&
    (!Object.values(pixelCrop).every(Number.isFinite) ||
      pixelCrop.width <= 0 ||
      pixelCrop.height <= 0 ||
      pixelCrop.x < 0 ||
      pixelCrop.y < 0 ||
      pixelCrop.x + pixelCrop.width > bBoxWidth ||
      pixelCrop.y + pixelCrop.height > bBoxHeight)
  ) {
    return null;
  }

  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement("canvas");

  const croppedCtx = croppedCanvas.getContext("2d");

  if (!croppedCtx) {
    return null;
  }

  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  return new Promise<File>((resolve, reject) => {
    try {
      croppedCanvas.toBlob((blob) => {
        const { type, lastModified, name } = imgFile;

        if (!blob) {
          throw new Error("Canvas is empty");
        }

        resolve(
          new File([blob], name, {
            type,
            lastModified,
          }),
        );
      });
    } catch (err) {
      reject(err);
    }
  });
}
